import { get, set } from 'idb-keyval'
import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

const STORAGE_BASE = import.meta.env.VITE_STORAGE_BASE || ''

/**
 * 动画文件信息
 */
export interface AnimationFile {
  /** 文件名（不含扩展名） */
  name: string
  /** 文件名（含扩展名） */
  filename: string
  /** API访问路径 */
  url: string
  /** 文件大小（字节） */
  size?: number
}

/**
 * 动画类别信息
 */
export interface AnimationCategory {
  /** 类别名称 */
  name: string
  /** 类别路径 */
  path: string
  /** 子类别 */
  subcategories?: AnimationCategory[]
  /** 该类别下的动画文件列表 */
  animations?: AnimationFile[]
}

/**
 * 动画库索引
 */
export interface AnimationLibraryIndex {
  /** 所有动画类别 */
  categories: AnimationCategory[]
  /** 动画总数 */
  totalAnimations: number
}

/**
 * 动画状态机状态
 */
export type AnimationState = 'greet' | 'idle' | 'custom'

/**
 * 动画管理Store
 *
 * 功能：
 * - 从服务器获取动画库索引
 * - IndexedDB缓存已下载的vrma文件
 * - 动画状态机管理（greet → idle）
 * - 随机选择动画
 */
export const useAnimationsStore = defineStore('animationsStore', () => {
  // 动画库索引（从服务器获取）
  const libraryIndex = shallowRef<AnimationLibraryIndex | null>(null)

  // 加载状态
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<string | null>(null)

  // 当前动画状态
  const currentState = ref<AnimationState>('greet')

  // 当前播放的动画URL
  const currentAnimationUrl = ref<string | null>(null)

  /**
   * 从IndexedDB获取缓存的动画数据
   */
  async function getCachedAnimation(url: string): Promise<ArrayBuffer | null> {
    try {
      const cached = await get<ArrayBuffer>(url)
      return cached ?? null
    }
    catch {
      return null
    }
  }

  /**
   * 将动画数据缓存到IndexedDB
   */
  async function cacheAnimation(url: string, data: ArrayBuffer): Promise<void> {
    try {
      await set(url, data)
    }
    catch {
      // 缓存失败不影响主流程
    }
  }

  /**
   * 从 R2 或服务器获取动画库索引
   */
  async function fetchLibraryIndex(apiBaseUrl: string): Promise<AnimationLibraryIndex> {
    // R2 直连：读取静态 animations.json，避免依赖 VPS
    const url = STORAGE_BASE
      ? `${STORAGE_BASE}/animations/animations.json`
      : `${apiBaseUrl}/api/v1/animations`
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to fetch animation library: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * 递归获取所有动画文件
   */
  function getAllAnimations(categories: AnimationCategory[]): AnimationFile[] {
    const result: AnimationFile[] = []

    for (const category of categories) {
      if (category.animations) {
        result.push(...category.animations)
      }
      if (category.subcategories) {
        result.push(...getAllAnimations(category.subcategories))
      }
    }

    return result
  }

  /**
   * 预缓存所有动画文件到IndexedDB
   * 只缓存未缓存的文件，避免重复下载
   */
  async function precacheAnimations(apiBaseUrl: string): Promise<void> {
    if (!libraryIndex.value) {
      return
    }

    const allAnimations = getAllAnimations(libraryIndex.value.categories)
    // 并行下载未缓存的动画
    const cachePromises = allAnimations.map(async (anim) => {
      const fullUrl = resolveAnimationUrl(apiBaseUrl, anim)

      // 检查是否已缓存
      const cached = await getCachedAnimation(fullUrl)
      if (cached) {
        return
      }

      // 从服务器下载
      try {
        const response = await fetch(fullUrl)
        if (!response.ok) {
          return
        }

        const data = await response.arrayBuffer()
        await cacheAnimation(fullUrl, data)
      }
      catch (e) {
        console.warn(`Error caching animation ${anim.filename}:`, e)
      }
    })

    // 等待所有缓存完成（不阻塞主流程）
    await Promise.all(cachePromises)
  }

  /**
   * 刷新动画库（页面加载时调用）
   * 同时预缓存所有动画文件
   */
  async function refreshLibrary(apiBaseUrl: string, precache: boolean = true): Promise<void> {
    if (isLoading.value) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const index = await fetchLibraryIndex(apiBaseUrl)
      libraryIndex.value = index
      isLoaded.value = true

      // 预缓存所有动画文件
      if (precache) {
        // 不阻塞主流程，后台缓存
        precacheAnimations(apiBaseUrl).catch((e) => {
          console.warn('Animation precache error:', e)
        })
      }
    }
    catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load animation library'
      throw e
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * 获取指定类别路径下的所有动画
   * 支持嵌套路径，如 'f-common/greet' 或 'f-common/idle/standing'
   */
  function getAnimationsByCategoryPath(path: string): AnimationFile[] {
    if (!libraryIndex.value) {
      return []
    }

    const parts = path.split('/')
    let current: AnimationCategory[] = libraryIndex.value.categories

    // 逐层遍历路径
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      const category = current.find((c: AnimationCategory) => c.name === part)

      if (!category) {
        return []
      }

      // 如果是最后一层，返回该层的动画
      if (i === parts.length - 1) {
        return category.animations || []
      }

      // 不是最后一层，继续进入子目录查找
      if (category.subcategories) {
        current = category.subcategories
      }
      else {
        // 没有子目录但还没到达目标路径
        return []
      }
    }

    return []
  }

  /**
   * 获取greet动画列表
   * @param gender 角色性别，默认为 'female'（目前greet动画只有f-common）
   */
  function getGreetAnimations(gender: 'female' | 'male' = 'female'): AnimationFile[] {
    // NOTICE: greet动画目前只有 f-common 有，参数保留以便未来扩展
    void gender // 参数保留以保持API一致性，未来可能添加m-common/greet
    return getAnimationsByCategoryPath('f-common/greet')
  }

  /**
   * 获取idle动画列表（包括standing和thinking）
   * @param gender 角色性别，默认为 'female'
   */
  function getIdleAnimations(gender: 'female' | 'male' = 'female'): AnimationFile[] {
    if (gender === 'female') {
      // f-common: idle动画在 standing/thinking 子目录下
      const standing = getAnimationsByCategoryPath('f-common/idle/standing')
      const thinking = getAnimationsByCategoryPath('f-common/idle/thinking')
      return [...standing, ...thinking]
    }
    else {
      // m-common: idle动画直接在 idle 目录下
      const idle = getAnimationsByCategoryPath('m-common/idle')
      return idle
    }
  }

  /**
   * 获取greet动画列表（计算属性，默认女性）
   */
  const greetAnimations = computed(() => {
    return getGreetAnimations('female')
  })

  /**
   * 获取idle动画列表（计算属性，默认女性）
   */
  const idleAnimations = computed(() => {
    return getIdleAnimations('female')
  })

  /**
   * 随机选择一个动画
   */
  function selectRandomAnimation(animations: AnimationFile[]): AnimationFile | null {
    if (animations.length === 0) {
      return null
    }

    const index = Math.floor(Math.random() * animations.length)
    return animations[index]
  }

  /**
   * 随机选择一个greet动画
   * @param gender 角色性别，默认为 'female'
   */
  function selectRandomGreet(gender: 'female' | 'male' = 'female'): AnimationFile | null {
    return selectRandomAnimation(getGreetAnimations(gender))
  }

  /**
   * 随机选择一个idle动画
   * @param gender 角色性别，默认为 'female'
   */
  function selectRandomIdle(gender: 'female' | 'male' = 'female'): AnimationFile | null {
    return selectRandomAnimation(getIdleAnimations(gender))
  }

  /**
   * 获取下一个动画（基于状态机）
   * greet完成后进入idle
   */
  function getNextAnimation(): AnimationFile | null {
    if (currentState.value === 'greet') {
      // greet完成后进入idle
      currentState.value = 'idle'
      return selectRandomIdle()
    }

    // idle状态下继续随机选择idle动画
    return selectRandomIdle()
  }

  /**
   * 初始化动画状态（首次交互时使用greet）
   */
  function initializeAnimationState(): AnimationFile | null {
    currentState.value = 'greet'
    const greet = selectRandomGreet()

    if (greet) {
      currentAnimationUrl.value = greet.url
    }

    return greet
  }

  /**
   * 下载并缓存动画文件，返回ArrayBuffer
   */
  async function loadAnimationData(apiBaseUrl: string, url: string): Promise<ArrayBuffer> {
    // 先检查缓存
    const cached = await getCachedAnimation(url)

    if (cached) {
      return cached
    }

    // 从服务器下载
    const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`
    const response = await fetch(fullUrl)

    if (!response.ok) {
      throw new Error(`Failed to download animation: ${response.statusText}`)
    }

    const data = await response.arrayBuffer()

    // 缓存到IndexedDB
    await cacheAnimation(url, data)

    return data
  }

  /**
   * 获取完整的动画URL（用于VRMModel组件）
   */
  function resolveAnimationUrl(apiBaseUrl: string, animation: AnimationFile): string {
    if (animation.url.startsWith('http')) {
      return animation.url
    }
    // 静态资源直连 R2，避免 VPS 中转 .vrma 二进制
    if (STORAGE_BASE) {
      // R2 animations.json 里的相对路径（f-common/idle/standing/standing_01.vrma）
      // 或 VPS API 路径（/api/v1/animations/...）都重定向到 R2
      const path = animation.url.replace(/^\/api\/v1\/animations\//, '')
      return `${STORAGE_BASE}/animations/${path}`
    }

    return `${apiBaseUrl}${animation.url}`
  }

  /**
   * 重置动画状态
   */
  function resetState(): void {
    currentState.value = 'greet'
    currentAnimationUrl.value = null
  }

  return {
    // 状态
    libraryIndex,
    isLoading,
    isLoaded,
    error,
    currentState,
    currentAnimationUrl,

    // 计算属性
    greetAnimations,
    idleAnimations,

    // 方法
    refreshLibrary,
    getAnimationsByCategoryPath,
    getGreetAnimations,
    getIdleAnimations,
    selectRandomAnimation,
    selectRandomGreet,
    selectRandomIdle,
    getNextAnimation,
    initializeAnimationState,
    loadAnimationData,
    resolveAnimationUrl,
    resetState,
    getCachedAnimation,
    cacheAnimation,
  }
})

export type AnimationsStore = ReturnType<typeof useAnimationsStore>
